/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect } from 'react';
import { HeroSpiceScene, FloatingPeppercornScene } from './components/QuantumScene';
import { Timeline, ProductionCycle, GradingSpecs, MapView } from './components/Diagrams';
import { ArrowDown, Menu, X, FileText, MapPin, Award, History } from 'lucide-react';

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4] text-pepper-black selection:bg-spice-gold selection:text-white">
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#F9F8F4]/90 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-10 h-10 bg-pepper-black rounded-full flex items-center justify-center text-spice-gold font-serif font-bold text-xl shadow-lg border border-spice-gold/20">GI</div>
            <div className="flex flex-col">
                <span className={`font-serif font-bold text-lg tracking-wide leading-none transition-opacity ${scrolled ? 'text-pepper-black' : 'text-pepper-black'}`}>
                MALABAR PEPPER
                </span>
                <span className="text-[10px] font-sans font-bold tracking-[0.2em] text-spice-brown uppercase">Application No. 49</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium tracking-wide text-stone-600">
            <a href="#identity" onClick={scrollToSection('identity')} className="hover:text-spice-gold transition-colors cursor-pointer uppercase flex items-center gap-2"><FileText size={16}/> Identity</a>
            <a href="#history" onClick={scrollToSection('history')} className="hover:text-spice-gold transition-colors cursor-pointer uppercase flex items-center gap-2"><History size={16}/> History</a>
            <a href="#geography" onClick={scrollToSection('geography')} className="hover:text-spice-gold transition-colors cursor-pointer uppercase flex items-center gap-2"><MapPin size={16}/> Region</a>
            <a href="#quality" onClick={scrollToSection('quality')} className="hover:text-spice-gold transition-colors cursor-pointer uppercase flex items-center gap-2"><Award size={16}/> Quality</a>
          </div>

          <button className="md:hidden text-pepper-black p-2" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-[#F9F8F4] flex flex-col items-center justify-center gap-8 text-xl font-serif animate-fade-in text-pepper-black">
            <a href="#identity" onClick={scrollToSection('identity')} className="uppercase">Identity</a>
            <a href="#history" onClick={scrollToSection('history')} className="uppercase">History</a>
            <a href="#geography" onClick={scrollToSection('geography')} className="uppercase">Region</a>
            <a href="#quality" onClick={scrollToSection('quality')} className="uppercase">Quality</a>
        </div>
      )}

      {/* Hero Section */}
      <header className="relative h-screen flex items-center justify-center overflow-hidden">
        <HeroSpiceScene />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 z-0 pointer-events-none bg-[radial-gradient(circle_at_center,rgba(249,248,244,0.85)_0%,rgba(249,248,244,0.5)_60%,rgba(249,248,244,0.1)_100%)]" />

        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 border border-spice-gold/50 text-spice-brown text-xs tracking-[0.2em] uppercase font-bold rounded-full backdrop-blur-sm bg-white/40 shadow-sm">
            <Award size={14} className="text-spice-gold" /> Geographical Indication Registry
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-9xl font-medium leading-tight md:leading-[0.9] mb-8 text-pepper-black drop-shadow-sm">
            Malabar <br/><span className="italic font-normal text-spice-brown/80 text-4xl md:text-6xl block mt-4">"The Black Gold of India"</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-stone-700 font-light leading-relaxed mb-12">
            Classified under Class 30. A unique variety of <i>Piper nigrum</i> originating from the historic Malabar Coast, renowned for its sharp, hot, and biting taste.
          </p>
          
          <div className="flex justify-center">
             <a href="#identity" onClick={scrollToSection('identity')} className="group flex flex-col items-center gap-2 text-sm font-medium text-spice-brown hover:text-pepper-black transition-colors cursor-pointer">
                <span>EXPLORE THE ORIGIN</span>
                <span className="p-2 border border-spice-brown/30 rounded-full group-hover:border-pepper-black transition-colors bg-white/50">
                    <ArrowDown size={16} />
                </span>
             </a>
          </div>
        </div>
      </header>

      <main>
        {/* Identity Section */}
        <section id="identity" className="py-24 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-spice-gold/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-16 items-start">
            <div className="md:col-span-5">
              <div className="inline-block mb-3 text-xs font-bold tracking-widest text-spice-brown uppercase">The Application</div>
              <h2 className="font-serif text-4xl md:text-5xl mb-8 leading-tight text-pepper-black">Official Identity</h2>
              
              <div className="bg-[#F9F8F4] p-8 border border-stone-200 rounded-lg shadow-sm">
                <div className="space-y-6">
                    <div>
                        <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Applicant</h4>
                        <p className="font-serif text-xl text-pepper-black">Spices Board</p>
                        <p className="text-sm text-stone-600">Ministry of Commerce & Industry, Govt. of India</p>
                    </div>
                    <div className="w-full h-[1px] bg-stone-200"></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Class</h4>
                            <p className="font-serif text-xl text-pepper-black">30</p>
                        </div>
                         <div>
                            <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Goods</h4>
                            <p className="font-serif text-xl text-pepper-black">Black Pepper</p>
                        </div>
                    </div>
                     <div className="w-full h-[1px] bg-stone-200"></div>
                     <div>
                        <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-1">Headquarters</h4>
                        <p className="text-sm text-stone-600">Sugandha Bhavan N.H. Bye-pass,<br/>Palarivattom, Cochin 25, Kerala</p>
                    </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-7 text-lg text-stone-600 leading-relaxed space-y-6 pt-8">
              <p>
                <span className="text-6xl float-left mr-4 mt-[-12px] font-serif text-spice-gold">M</span>alabar Pepper is one of the oldest and most important spices in the world. Often referred to as "Black Gold," it played a pivotal role in shaping the history of global trade.
              </p>
              <p>
                The Malabar variety is distinguished by its <strong>high aromatic quality</strong> and a perfect combination of flavour and aroma. It possesses a sharp, hot, and biting taste that is warming and distinctive. The peppercorns are small drupes, dark red when mature, which turn into the characteristic wrinkled black spheres upon drying.
              </p>
              <p className="italic border-l-2 border-spice-gold pl-4 text-stone-500">
                  "Malabar has always been considered to produce the best pepper." — Linschotten (1598)
              </p>
            </div>
          </div>
        </section>

        {/* History Section */}
        <section id="history" className="py-24 bg-[#1a1a1a] text-stone-200 overflow-hidden relative">
            <div className="absolute inset-0 opacity-20 pointer-events-none">
                 <div className="absolute top-[10%] left-[5%] w-96 h-96 bg-spice-gold rounded-full blur-[150px]"></div>
            </div>
            <div className="container mx-auto px-6 relative z-10">
                <div className="text-center mb-16">
                    <div className="inline-block mb-3 text-xs font-bold tracking-widest text-spice-gold uppercase">Proof of Origin</div>
                    <h2 className="font-serif text-4xl md:text-5xl mb-4 text-white">The Spice Route</h2>
                    <p className="text-stone-400 max-w-2xl mx-auto">Historical records linking the origin of pepper to the Malabar Coast.</p>
                </div>
                
                <Timeline />
                
            </div>
        </section>

        {/* Geography & Process */}
        <section id="geography" className="py-24 bg-[#F5F4F0] border-t border-stone-200">
             <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    {/* Map Side */}
                    <div className="lg:col-span-5">
                         <div className="inline-block mb-3 text-xs font-bold tracking-widest text-stone-500 uppercase">Geographical Area</div>
                         <h2 className="font-serif text-4xl mb-6 text-pepper-black">The Malabar Coast</h2>
                         <p className="text-stone-600 mb-8 leading-relaxed">
                             The area of production covers the composite states comprised in the Malabar region of the erstwhile Madras Presidency, which today covers South India including Kerala, parts of Karnataka, and Tamil Nadu.
                         </p>
                         <MapView />
                    </div>

                    {/* Process Side */}
                    <div className="lg:col-span-7">
                        <div className="inline-block mb-3 text-xs font-bold tracking-widest text-stone-500 uppercase">Method of Production</div>
                         <h2 className="font-serif text-4xl mb-6 text-pepper-black">From Vine to Table</h2>
                         <p className="text-stone-600 mb-8 leading-relaxed">
                            Malabar pepper is grown in soil that is moist, well-drained, and rich in organic matter. The plants are propagated by cuttings and climb neighboring trees.
                         </p>
                         <ProductionCycle />
                    </div>
                </div>
             </div>
        </section>

        {/* Quality Specs */}
        <section id="quality" className="py-24 bg-white border-t border-stone-200">
            <div className="container mx-auto px-6">
                 <div className="max-w-4xl mx-auto text-center mb-16">
                    <div className="inline-block mb-3 text-xs font-bold tracking-widest text-stone-500 uppercase">Specification</div>
                    <h2 className="font-serif text-4xl md:text-5xl mb-6 text-pepper-black">Grading Standards</h2>
                    <p className="text-lg text-stone-600 leading-relaxed">
                        Malabar Pepper is classified into "Garbled" and "Un-garbled" varieties. The highest quality Garbled grades (MG Grade 1 & 2) must meet strict standards for moisture and extraneous matter.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="order-2 lg:order-1 h-[400px] bg-stone-100 rounded-xl overflow-hidden relative border border-stone-200 shadow-inner">
                        <FloatingPeppercornScene />
                        <div className="absolute bottom-4 left-0 right-0 text-center text-xs text-stone-500 font-serif italic">
                            MG Grade 1: Dark brown to black, nearly globular with wrinkled surface
                        </div>
                    </div>
                    <div className="order-1 lg:order-2">
                        <GradingSpecs />
                    </div>
                </div>
            </div>
        </section>

      </main>

      <footer className="bg-pepper-black text-stone-400 py-16 border-t border-stone-800">
        <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="text-center md:text-left">
                <div className="text-white font-serif font-bold text-2xl mb-2">Malabar Pepper</div>
                <p className="text-sm text-stone-500">Geographical Indication Registry Application No. 49</p>
            </div>
             <div className="flex gap-6 text-sm">
                <span className="hover:text-spice-gold cursor-pointer transition-colors">Spices Board</span>
                <span className="hover:text-spice-gold cursor-pointer transition-colors">Ministry of Commerce</span>
                <a href="https://search.ipindia.gov.in/GIRPublic/Application/Details/49" target="_blank" rel="noreferrer" className="hover:text-spice-gold cursor-pointer transition-colors">View Official Record</a>
            </div>
        </div>
        <div className="text-center mt-12 text-xs text-stone-600">
            Visualized based on the official GI Application document.
        </div>
      </footer>
    </div>
  );
};

export default App;